import { ladderedUnitDisplay, unitLabel } from '#core/components/dashboard/unit-label.ts';
import { type ColumnType, displayUnitOfType, numberCol, withUnit } from '#core/lib/expr/schema/types.ts';
import { unitOf } from '#core/lib/units/unit.ts';

/**
 * Decimal places used when rendering numbers into the LLM-facing tool
 * message. Declared as a const so the rounding policy can be tuned in
 * one place. Raw dataset values are never rounded — only the string
 * form that goes into the LLM context.
 */
export const LLM_MESSAGE_DECIMAL_PLACES = 2;

/**
 * Round a number to `LLM_MESSAGE_DECIMAL_PLACES` decimals for LLM-facing
 * display. Non-finite values (`NaN`, `±Infinity`) pass through unchanged
 * so callers can render them verbatim.
 *
 * The result is a plain number: `String(roundForDisplay(1.5))` yields
 * `"1.5"` (no trailing zeros).
 */
export function roundForDisplay(value: number): number {
  if (!Number.isFinite(value)) {
    return value;
  }
  const factor = 10 ** LLM_MESSAGE_DECIMAL_PLACES;
  return Math.round(value * factor) / factor;
}

/**
 * JSON-round-trippable carrier for a column's declared unit, persisted on a
 * {@link DatasetColumnSummary}. The engine {@link ColumnType.unit} is a
 * `ReadonlyMap` (a dimension vector) and `JSON.stringify` would flatten a `Map`
 * to `{}`, dropping the unit through the localStorage transcript round-trip —
 * so the summary stores the map's `[baseSlug, exponent]` entries as a plain
 * array instead. `undefined` for a unit-less / dimensionless column.
 *
 * {@link reconstructValueType} rebuilds an engine `ColumnType` from these
 * entries so the core SSOT formatters ({@link ladderedUnitDisplay},
 * {@link unitLabel}) — which read the unit via `unitOfType` — can be reused
 * verbatim, exactly as the KPI `MetricPanel` does off a live schema.
 */
export type SerializableUnit = readonly (readonly [string, number])[] | undefined;

/**
 * Extract a column's declared unit as a {@link SerializableUnit} — the SSOT for
 * the engine `ColumnType` → persisted-summary direction. `undefined` for a
 * unit-less / dimensionless column. Uses {@link displayUnitOfType}, so a
 * timeseries-reference column contributes the unit of its inner datapoint value
 * (the same accessor the schema-doc display path uses). Pairs with
 * {@link reconstructValueType} (the inverse), keeping the entries shape defined
 * in exactly one place.
 */
export const serializableUnitOf = (type: ColumnType): SerializableUnit => {
  const unit = displayUnitOfType(type);
  return unit === undefined ? undefined : Array.from(unit.entries());
};

/**
 * Rebuild a numeric engine `ColumnType` carrying `unit`'s dimension vector, so
 * the core unit formatters can be applied to a persisted summary. A unit-less
 * column reconstructs to the bare `numberCol`.
 */
const reconstructValueType = (unit: SerializableUnit): ColumnType =>
  unit === undefined ? numberCol : withUnit(numberCol, unitOf(unit));

/**
 * A per-column, unit-aware prose number formatter. `format(v)` renders a single
 * canonical-base magnitude as `"<mantissa> <unit>"`, with the unit-less case
 * collapsing to a bare rounded number. `formatValue` / `unit` expose the same
 * rung's parts separately, so a range can carry the unit once (`350–408 MSTB/d`)
 * and the Type cell can label itself with the identical unit string.
 */
export interface ColumnUnitFormatter {
  /** Render one canonical-base magnitude as `"<mantissa> <unit>"` (bare number when unit-less). */
  readonly format: (vBase: number) => string;
  /** Render one canonical-base magnitude as a bare mantissa on the shared rung, no unit. */
  readonly formatValue: (vBase: number) => string;
  /** The shared rung's unit label (e.g. `MSTB/d`); empty string when unit-less. */
  readonly unit: string;
}

/**
 * SSOT for unit-aware prose number formatting in dataset summaries. Mirrors the
 * KPI `MetricPanel` pattern: a laddered (petroleum) dimension folds magnitude
 * into the unit prefix on a SHARED rung fixed from `values`, so every magnitude
 * of one column stays on the same rung and comparable; a fixed-symbol dimension
 * (pressure, money, …) appends its magnitude-independent {@link unitLabel}; a
 * unit-less column renders a bare {@link roundForDisplay} number.
 *
 * Pass the SAME `values` array across all of a column's magnitudes (stats and
 * insights) to keep the chosen rung consistent.
 */
export const columnUnitFormatter = (unit: SerializableUnit, values: readonly number[]): ColumnUnitFormatter => {
  const valueType = reconstructValueType(unit);
  const laddered = ladderedUnitDisplay(valueType, values);
  if (laddered) {
    return {
      formatValue: (vBase) => laddered.format(vBase),
      unit: laddered.unit,
      format: (vBase) => `${laddered.format(vBase)} ${laddered.unit}`,
    };
  }
  const label = unitLabel(valueType) ?? '';
  return {
    formatValue: (vBase) => String(roundForDisplay(vBase)),
    unit: label,
    format: (vBase) => (label ? `${roundForDisplay(vBase)} ${label}` : String(roundForDisplay(vBase))),
  };
};
